import type { AlignmentFlag } from './api';

/**
 * Pure state machine for the alignment-review deck's ruling interaction —
 * mirrors dragRehome.ts's exact shape (apply optimistic -> confirm | rollback,
 * "keep the whole pre-change snapshot, not a diff" discipline) rather than
 * inventing a new pattern, since a ruling IS a rehome-adjacent mutation for
 * 'moved' (the card's roleId changes) and a pure removal for 'kept' /
 * 'flagged_split' (the card leaves the review deck either way — a ruling is
 * ALWAYS terminal, so every ruling value optimistically removes the flag from
 * the visible deck, never just the 'moved' case).
 *
 * Lifecycle:
 *   idle -> pending (the flag optimistically removed from the list, snapshot kept)
 *   pending -> idle (confirmed: server accepted the ruling, snapshot discarded)
 *   pending -> idle (rolled back: server rejected, the flag reappears)
 *
 * Only one flag may be mid-ruling at a time per the UX (one tap, one card) —
 * the caller (useAlignmentReview) enforces this by disabling other cards'
 * actions while a ruling is pending, mirroring dragRehome's "one card at a
 * time" note.
 *
 * Kept framework-free (no React) so the whole lifecycle is unit-testable
 * without mounting a component.
 */

export interface AlignmentReviewRulingState {
  /** The achievementId currently mid-ruling, or null when idle. */
  pendingAchievementId: string | null;
  /** The FULL flags array from just before the optimistic removal, so a
   *  failure can restore it byte-for-byte. Null when idle. */
  snapshot: AlignmentFlag[] | null;
}

export const INITIAL_ALIGNMENT_REVIEW_RULING_STATE: AlignmentReviewRulingState = {
  pendingAchievementId: null,
  snapshot: null,
};

/** Apply the optimistic ruling: remove `achievementId`'s flag from the list
 *  (a ruling is ALWAYS terminal — the card leaves the review deck the instant
 *  the user decides, regardless of which of the three rulings they picked),
 *  returning both the next flags list and the state needed to roll back. */
export function applyOptimisticRuling(
  flags: AlignmentFlag[],
  achievementId: string,
): { next: AlignmentFlag[]; state: AlignmentReviewRulingState } {
  const next = flags.filter((f) => f.achievementId !== achievementId);
  return {
    next,
    state: { pendingAchievementId: achievementId, snapshot: flags },
  };
}

/** The server confirmed — drop the snapshot; the optimistic removal stands
 *  (the ruling is terminal, so there is nothing further to reconcile — unlike
 *  a drag-rehome, a ruling never mints a "new card" the deck needs to show;
 *  it only ever REMOVES one). */
export function confirmRuling(): AlignmentReviewRulingState {
  return INITIAL_ALIGNMENT_REVIEW_RULING_STATE;
}

/** The server rejected (or the request threw) — restore the pre-ruling
 *  snapshot so the flagged card reappears in the deck. Returns null for
 *  `restored` if called with no pending snapshot (a caller bug, not a
 *  user-facing error), mirroring rollbackRehome's identical guard. */
export function rollbackRuling(state: AlignmentReviewRulingState): {
  restored: AlignmentFlag[] | null;
  state: AlignmentReviewRulingState;
} {
  return { restored: state.snapshot, state: INITIAL_ALIGNMENT_REVIEW_RULING_STATE };
}

/** Whether a flag is currently mid-ruling (its own actions + every other
 *  card's actions disable while true, matching the "one at a time" UX). */
export function isRulingPending(state: AlignmentReviewRulingState): boolean {
  return state.pendingAchievementId !== null;
}
